import React, { useCallback, useState } from 'react';
import { Box, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { alpha, type SxProps, type Theme } from '@mui/material/styles';
import { ReactComponent as CloudDownloadIcon } from '../../static/images/svg/iconamoon_cloud-download-fill.svg';

/**
 * 数据分析等页面顶部：云下载式导出，点击后可选同步预检；预检通过则进入 loading 直至 onExport 结束（支持异步）。
 */
export type DataExportIconButtonProps = {
    /** Tooltip 与 `aria-label`，应使用 i18n 文案 */
    title: string;
    /**
     * 请求前的同步预检。返回 `false` 时：不进入 loading、不执行 `onExport`（可在此发「暂无数据」等提示）。
     * 例如：与列表同源数据已在前序请求中加载时，可在此判断全空则不必再拉导出接口。
     */
    preCheck?: () => boolean;
    /** 导出逻辑，可为异步（如带 cancelToken 的请求） */
    onExport: () => void | Promise<void>;
    /** 为 true 时与进行中一样不可点，例如列表仍在加载、避免用空表误判为无数据 */
    disabled?: boolean;
    /** 透传至 `IconButton` 的 `sx` */
    sx?: SxProps<Theme>;
    /** 主图标，默认使用云下载 SVG */
    icon?: React.ReactNode;
    /** 加载圈直径（px），与 `LoadingOverlayButton` 默认一致 */
    loadingIndicatorSize?: number;
};

const DEFAULT_LOADING_PX = 20;

/**
 * 通用数据导出图标按钮：Tooltip + 防连发；loading 时保留图标，上覆半透明遮罩与菊花（对齐 `loading-overlay-button`）。
 * @param props - 见 {@link DataExportIconButtonProps}
 */
export function DataExportIconButton(props: DataExportIconButtonProps): JSX.Element {
    const { title, preCheck, onExport, disabled = false, sx, icon, loadingIndicatorSize = DEFAULT_LOADING_PX } = props;
    const [pending, setPending] = useState(false);

    const handleClick = useCallback(async () => {
        if (disabled || pending) {
            return;
        }
        if (preCheck && !preCheck()) {
            return;
        }
        setPending(true);
        try {
            await onExport();
        } finally {
            setPending(false);
        }
    }, [disabled, onExport, preCheck, pending]);

    const busy = disabled || pending;

    return (
        <Tooltip title={title} placement="top">
            <span>
                <IconButton
                    color="primary"
                    aria-label={title}
                    aria-busy={pending}
                    disabled={busy}
                    onClick={handleClick}
                    sx={[
                        {
                            position: 'relative',
                            overflow: 'hidden',
                            p: 0,
                        },
                        ...(sx != null ? (Array.isArray(sx) ? sx : [sx]) : []),
                    ]}
                >
                    {pending ? (
                        <Box
                            aria-hidden
                            sx={(theme) => ({
                                position: 'absolute',
                                inset: 0,
                                zIndex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 'inherit',
                                backgroundColor: alpha(theme.palette.common.white, 0.2),
                            })}
                        >
                            <CircularProgress
                                color="primary"
                                size={loadingIndicatorSize}
                                thickness={4}
                            />
                        </Box>
                    ) : null}
                    {icon ?? <CloudDownloadIcon />}
                </IconButton>
            </span>
        </Tooltip>
    );
}
